import {
  TextField,
  MenuItem,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
} from "@material-ui/core";
import {
  ClientTranscriptSegmentElement,
  TranscriptSegmentElement,
  TxSegmentElementResponse,
} from "../../../../../services/ResponseModels";
import React, { useEffect, useState, KeyboardEvent, ChangeEvent } from "react";
import {
  GenomicInputType,
  getTxSegmentElementEC,
  getTxSegmentElementGC,
  getTxSegmentNomenclature,
  TxElementInputType,
} from "../../../../../services/main";
import { GeneAutocomplete } from "../../../../main/shared/GeneAutocomplete/GeneAutocomplete";
import { StructuralElementInputProps } from "../StructuralElementInputProps";
import StructuralElementInputAccordion from "../StructuralElementInputAccordion";
import HelpTooltip from "../../../../main/shared/HelpTooltip/HelpTooltip";
import ChromosomeField from "../../../../main/shared/ChromosomeField/ChromosomeField";
import TranscriptField from "../../../../main/shared/TranscriptField/TranscriptField";
import StrandSwitch from "../../../../main/shared/StrandSwitch/StrandSwitch";
import GeneTranscriptSelector from "../../../../main/shared/GeneTranscriptSelector/GeneTranscriptSelector";
import { TxGenomicCoords } from "../../../../main/shared/TxGenomicCoords/TxGenomicCoords";
import { setNumericField } from "../../../../Utilities/SetNumericField/SetNumericField";

interface TxSegmentElementInputProps extends StructuralElementInputProps {
  element: ClientTranscriptSegmentElement;
}

const TxSegmentCompInput: React.FC<TxSegmentElementInputProps> = ({
  element,
  handleSave,
  handleDelete,
  icon,
}) => {
  const [txInputType, setTxInputType] = useState<TxElementInputType>(
    (element.inputType as TxElementInputType) || TxElementInputType.default
  );

  const [genomicInputType, setGenomicInputType] =
    useState<GenomicInputType | null>(
      element.inputGene || element.gene
        ? GenomicInputType.GENE
        : element.inputTx
        ? GenomicInputType.TRANSCRIPT
        : null
    );

  const [errors, setErrors] = useState<string[]>([]);

  // "Text" variables refer to helper or warning text to set under input fields
  // TODO: this needs refactored so badly
  const [txAc, setTxAc] = useState(element.inputTx || "");
  const [txAcHelperText, setTxAcHelperText] = useState("");

  const [txGene, setTxGene] = useState(element.inputGene || "");
  const [txGeneText, setTxGeneText] = useState("");

  const [txStrand, setTxStrand] = useState<string>(
    element.inputStrand === 1 ? "+" : "-"
  );

  const [txChrom, setTxChrom] = useState(element.inputChr || "");

  const [txStartingGenomic, setTxStartingGenomic] = useState(
    element.inputGenomicStart || ""
  );
  const [txStartingGenomicText, setTxStartingGenomicText] = useState("");
  const [txEndingGenomic, setTxEndingGenomic] = useState(
    element.inputGenomicEnd || ""
  );
  const [txEndingGenomicText, setTxEndingGenomicText] = useState("");

  const [startingExon, setStartingExon] = useState(element.exonStart || "");
  const [startingExonText, setStartingExonText] = useState("");
  const [endingExon, setEndingExon] = useState(element.exonEnd || "");
  const [endingExonText, setEndingExonText] = useState("");
  const [startingExonOffset, setStartingExonOffset] = useState(
    element.exonStartOffset || ""
  );
  const [startingExonOffsetText, setStartingExonOffsetText] = useState("");
  const [endingExonOffset, setEndingExonOffset] = useState(
    element.exonEndOffset || ""
  );
  const [endingExonOffsetText, setEndingExonOffsetText] = useState("");

  const [geneTranscripts, setGeneTranscripts] = useState([]);

  const [pendingResponse, setPendingResponse] = useState(false);

  const hasRequiredEnds =
    txEndingGenomic || endingExon || txStartingGenomic || startingExon;

  const genomicInputComplete =
    genomicInputType === GenomicInputType.GENE
      ? txGene !== "" && txAc !== ""
      : txAc !== "";

  // programming horror
  const inputComplete =
    (txInputType === TxElementInputType.gc &&
      genomicInputComplete &&
      txChrom !== "" &&
      (txStartingGenomic !== "" || txEndingGenomic !== "")) ||
    (txInputType === TxElementInputType.ec &&
      txAc !== "" &&
      (startingExon !== "" || endingExon !== ""));

  const validated: boolean =
    inputComplete &&
    hasRequiredEnds &&
    txGeneText === "" &&
    txAcHelperText === "" &&
    txStartingGenomicText === "" &&
    txEndingGenomicText === "" &&
    startingExonText === "" &&
    startingExonOffsetText === "" &&
    endingExonText === "" &&
    endingExonOffsetText === "";

  const [expanded, setExpanded] = useState<boolean>(!validated);

  useEffect(() => {
    if (inputComplete) {
      buildTranscriptSegmentElement();
    }
  }, [
    txAc,
    txGene,
    txStrand,
    txChrom,
    txStartingGenomic,
    txEndingGenomic,
    startingExon,
    endingExon,
    startingExonOffset,
    endingExonOffset,
  ]);

  const handleTxElementResponse = (
    txSegmentResponse: TxSegmentElementResponse,
    inputParams: Record<string, string>
  ) => {
    const responseElement =
      txSegmentResponse.element as TranscriptSegmentElement;
    const finishedElement: ClientTranscriptSegmentElement = {
      ...element,
      ...responseElement,
      ...inputParams,
    };
    if (responseElement?.strand) {
      setTxStrand(responseElement.strand === 1 ? "+" : "-");
    }
    if (!hasRequiredEnds) {
      finishedElement.nomenclature = "ERROR";
    } else {
      getTxSegmentNomenclature(responseElement).then((nomenclatureResponse) => {
        if (
          !nomenclatureResponse.warnings &&
          nomenclatureResponse.nomenclature
        ) {
          finishedElement.nomenclature = nomenclatureResponse.nomenclature;
          handleSave(finishedElement);
        }
      });
    }
    setPendingResponse(false);
  };

  const handleTranscriptSelect = (event: any) => {
    setTxAc(event.target.value as string);
  };

  /**
   * Check for, and handle, warnings about invalid genomic coord values. Called by
   * constructor methods.
   * @param responseWarnings warnings property of transcript segment response object
   */
  const CheckGenomicCoordWarning = (responseWarnings: string[]) => {
    if (txStartingGenomic !== "") {
      const warning = `Unable to find a result for chromosome ${txChrom} where genomic coordinate ${txStartingGenomic} is mapped between an exon's start and end coordinates`;
      if (responseWarnings.find((e) => e.startsWith(warning))) {
        setTxStartingGenomicText("Out of range");
      }
    }
    if (txEndingGenomic !== "") {
      const warning = `Unable to find a result for chromosome ${txChrom} where genomic coordinate ${txEndingGenomic} is mapped between an exon's start and end coordinates`;
      if (responseWarnings.find((e) => e.startsWith(warning))) {
        setTxEndingGenomicText("Out of range");
      }
    }
    setPendingResponse(false);
  };

  /**
   * Reset warnings related to genomic coordinate values
   */
  const clearGenomicCoordWarnings = () => {
    setTxStartingGenomicText("");
    setTxEndingGenomicText("");
  };

  /**
   * Reset warnings related to exon coordinate values
   */
  const clearExonWarnings = () => {
    setStartingExonText("");
    setStartingExonOffsetText("");
    setEndingExonText("");
    setEndingExonOffsetText("");
  };

  /**
   * Request construction of tx segment element from server and handle response
   */
  const buildTranscriptSegmentElement = () => {
    setPendingResponse(true);
    // fire constructor request
    switch (txInputType) {
      case TxElementInputType.gc:
        clearGenomicCoordWarnings();
        getTxSegmentElementGC(
          txGene,
          txChrom,
          txAc,
          txStartingGenomic,
          txEndingGenomic
        ).then((txSegmentResponse) => {
          if (
            txSegmentResponse.warnings &&
            txSegmentResponse.warnings?.length > 0
          ) {
            setErrors(txSegmentResponse.warnings);
            CheckGenomicCoordWarning(txSegmentResponse.warnings);
          } else {
            const inputParams = {
              inputType: txInputType,
              inputStrand: txStrand,
              inputGene: txGene,
              inputTx: txAc,
              inputChr: txChrom,
              inputGenomicStart: txStartingGenomic,
              inputGenomicEnd: txEndingGenomic,
            };
            setErrors([]);
            handleTxElementResponse(txSegmentResponse, inputParams);
          }
        });
        break;
      case TxElementInputType.ec:
        getTxSegmentElementEC(
          txAc,
          startingExon as string,
          endingExon as string,
          startingExonOffset as string,
          endingExonOffset as string
        ).then((txSegmentResponse) => {
          if (
            txSegmentResponse.warnings &&
            txSegmentResponse.warnings?.length > 0
          ) {
            // transcript invalid
            const txWarning = `Unable to get exons for ${txAc}`;
            if (txSegmentResponse.warnings.includes(txWarning)) {
              setTxAcHelperText("Unrecognized value");
            }
            // exon(s) invalid
            if (startingExon !== undefined) {
              const startWarning = `Exon ${startingExon} does not exist on ${txAc}`;
              if (txSegmentResponse.warnings.includes(startWarning)) {
                setStartingExonText("Invalid");
              }
            }
            if (endingExon !== undefined) {
              const endWarning = `Exon ${endingExon} does not exist on ${txAc}`;
              if (txSegmentResponse.warnings.includes(endWarning)) {
                setEndingExonText("Invalid");
              }
            }
            setPendingResponse(false);
            setErrors(txSegmentResponse.warnings);
          } else {
            setTxAcHelperText("");
            setStartingExonText("");
            setEndingExonText("");
            setErrors([]);
            const inputParams = {
              inputType: txInputType,
              inputTx: txAc,
            };
            handleTxElementResponse(txSegmentResponse, inputParams);
          }
        });
    }
  };

  const handleEnterKey = (e: KeyboardEvent) => {
    if (e.key == "Enter" && validated) {
      setExpanded(false);
    }
  };

  const txInputField = (
    <Box className="mid-inputs" minWidth="255px">
      <TranscriptField
        fieldValue={txAc}
        valueSetter={setTxAc}
        errorText={txAcHelperText}
        keyHandler={handleEnterKey}
      />
    </Box>
  );

  const handleChromosomeChange = (
    e: ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    setTxChrom(e.target.value as string);
  };

  const genomicCoordinateInfo = (
    <>
      <Box className="mid-inputs">
        <ChromosomeField
          fieldValue={txChrom}
          onChange={handleChromosomeChange}
        />
        <Box mt="18px" width="125px">
          <StrandSwitch setStrand={setTxStrand} selectedStrand={txStrand} />
        </Box>
      </Box>
      <Box className="bottom-inputs">
        <TxGenomicCoords
          component={"transcript segment"}
          genomicStart={txStartingGenomic}
          genomicEnd={txEndingGenomic}
          txStartingGenomicText={txStartingGenomicText}
          txEndingGenomicText={txEndingGenomicText}
          setTxStartingGenomicText={setTxStartingGenomicText}
          setTxEndingGenomicText={setTxEndingGenomicText}
          setGenomicStart={setTxStartingGenomic}
          setGenomicEnd={setTxEndingGenomic}
          setNumericField={setNumericField}
          handleEnterKey={handleEnterKey}
        />
      </Box>
    </>
  );

  const renderTxOptions = () => {
    switch (txInputType) {
      case TxElementInputType.gc:
        if (!genomicInputType) {
          return <></>;
        }
        return (
          <Box>
            <Box className="mid-inputs" minWidth="325px">
              {genomicInputType === GenomicInputType.GENE ? (
                <>
                  <GeneAutocomplete
                    gene={txGene}
                    setGene={setTxGene}
                    tooltipDirection="bottom"
                    geneText={txGeneText}
                    setGeneText={setTxGeneText}
                    setChromosome={setTxChrom}
                    setStrand={setTxStrand}
                    setTranscripts={setGeneTranscripts}
                    setDefaultTranscript={setTxAc}
                  />
                  <GeneTranscriptSelector
                    transcript={txAc}
                    onTranscriptChange={handleTranscriptSelect}
                    transcripts={geneTranscripts}
                  />
                </>
              ) : (
                <>
                  <Box>{txInputField}</Box>
                </>
              )}
            </Box>
            {genomicCoordinateInfo}
          </Box>
        );
      case TxElementInputType.ec:
        return (
          <Box>
            <Box className="mid-inputs" minWidth="325px">
              <GeneAutocomplete
                gene={txGene}
                setGene={setTxGene}
                tooltipDirection="bottom"
                geneText={txGeneText}
                setGeneText={setTxGeneText}
                setChromosome={setTxChrom}
                setStrand={setTxStrand}
                setTranscripts={setGeneTranscripts}
                setDefaultTranscript={setTxAc}
              />

              <GeneTranscriptSelector
                transcript={txAc}
                onTranscriptChange={handleTranscriptSelect}
                transcripts={geneTranscripts}
              />
            </Box>
            <Box className="bottom-inputs">
              <HelpTooltip
                placement="bottom"
                title={
                  <Typography>
                    The starting exon number counted from the 5&#39; end of the
                    transcript.
                  </Typography>
                }
              >
                <TextField
                  margin="dense"
                  style={{ width: 125 }}
                  label="Starting Exon"
                  value={startingExon}
                  onChange={(event) =>
                    setNumericField(
                      event.target.value,
                      setStartingExonText,
                      setStartingExon,
                      true
                    )
                  }
                  onKeyDown={handleEnterKey}
                  error={startingExonText !== ""}
                  helperText={startingExonText !== "" ? startingExonText : null}
                />
              </HelpTooltip>
              <HelpTooltip
                placement="bottom"
                title={
                  <Typography>
                    The ending exon number counted from the 5&#39; end of the
                    transcript.
                  </Typography>
                }
              >
                <TextField
                  margin="dense"
                  style={{ width: 125 }}
                  label="Ending Exon"
                  value={endingExon}
                  onChange={(event) =>
                    setNumericField(
                      event.target.value,
                      setEndingExonText,
                      setEndingExon,
                      true
                    )
                  }
                  onKeyDown={handleEnterKey}
                  error={endingExonText !== ""}
                  helperText={endingExonText !== "" ? endingExonText : null}
                />
              </HelpTooltip>
            </Box>
            {startingExon !== "" || endingExon !== "" ? (
              <Box className="bottom-inputs">
                <HelpTooltip
                  placement="bottom"
                  title={
                    <Typography>
                      A value representing the offset from the segment boundary,
                      with negative values offset towards the 5’ end of the
                      transcript and positive values offset towards the 3’ end
                      of the transcript. Optional.
                    </Typography>
                  }
                >
                  <TextField
                    margin="dense"
                    style={{ width: 125 }}
                    label="Starting Offset"
                    value={startingExonOffset}
                    onChange={(event) =>
                      setNumericField(
                        event.target.value,
                        setStartingExonOffsetText,
                        setStartingExonOffset,
                        true
                      )
                    }
                    onKeyDown={handleEnterKey}
                    error={startingExonOffsetText !== ""}
                    helperText={
                      startingExonOffsetText !== ""
                        ? startingExonOffsetText
                        : null
                    }
                  />
                </HelpTooltip>
                <HelpTooltip
                  placement="bottom"
                  title={
                    <Typography>
                      A value representing the offset from the segment boundary,
                      with negative values offset towards the 5’ end of the
                      transcript and positive values offset towards the 3’ end
                      of the transcript. Optional.
                    </Typography>
                  }
                >
                  <TextField
                    margin="dense"
                    style={{ width: 125 }}
                    label="Ending Offset"
                    value={endingExonOffset}
                    onChange={(event) =>
                      setNumericField(
                        event.target.value,
                        setEndingExonOffsetText,
                        setEndingExonOffset,
                        true
                      )
                    }
                    onKeyDown={handleEnterKey}
                    error={endingExonOffsetText !== ""}
                    helperText={
                      endingExonOffsetText !== "" ? endingExonOffsetText : null
                    }
                  />
                </HelpTooltip>
              </Box>
            ) : null}
          </Box>
        );
    }
  };

  /**
   * Wrapper around input type selection to ensure unused inputs/warnings get cleared
   * @param selection selection from input type dropdown menu
   */
  const selectInputType = (selection: TxElementInputType) => {
    if (txInputType !== "default") {
      if (selection === "exon_coords") {
        clearGenomicCoordWarnings();
      } else {
        clearExonWarnings();
      }
    }
    setTxInputType(selection);
  };

  const inputElements = (
    <>
      <Box className="top-inputs">
        <HelpTooltip
          placement="left"
          title={
            <>
              <Typography>
                Select the types of location data to provide for constructing
                this transcript segment.
              </Typography>
              <Typography>
                Regardless of what kinds of data you provide, we can generate
                exhaustive genomic and transcript-level location information for
                this element.
              </Typography>
            </>
          }
        >
          <FormControl variant="standard">
            <InputLabel id="select-input-data">Select input data</InputLabel>
            <Select
              labelId="select-input-data"
              label="Select input data"
              value={txInputType}
              onChange={(event) =>
                selectInputType(event.target.value as TxElementInputType)
              }
            >
              <MenuItem value="default" disabled>
                Select input data
              </MenuItem>
              <MenuItem value="genomic_coords">Genomic coordinates</MenuItem>
              <MenuItem value="exon_coords">Exon coordinates</MenuItem>
            </Select>
            {txInputType === TxElementInputType.gc ? (
              <FormControl fullWidth style={{ marginTop: "10px" }}>
                <InputLabel id="genomic-input-type">
                  Gene or Transcript?
                </InputLabel>
                <Select
                  labelId="genomic-input-type"
                  value={genomicInputType}
                  onChange={(event) =>
                    setGenomicInputType(event.target.value as GenomicInputType)
                  }
                >
                  <MenuItem value="gene">Gene</MenuItem>
                  <MenuItem value="transcript">Transcript</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <></>
            )}
          </FormControl>
        </HelpTooltip>
      </Box>
      {renderTxOptions()}
    </>
  );

  return StructuralElementInputAccordion({
    expanded,
    setExpanded,
    element,
    handleDelete,
    inputElements,
    validated,
    errors,
    icon,
    pendingResponse,
  });
};

export default TxSegmentCompInput;
